import { useState, useEffect } from 'preact/hooks';
import { getConfig } from '../config';
import { getChatStatus } from '../api';

export type ChatStatus = 'online' | 'offline' | 'unknown';

/**
 * Singleton poller — shared across all useChatStatus() consumers.
 * Only polls for native provider; external providers (PHPLive etc.)
 * handle their own online/offline UI — we don't interfere.
 */
let sharedStatus: ChatStatus = 'unknown';
let subscribers = new Set<(s: ChatStatus) => void>();
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollStarted = false;

function notify(s: ChatStatus) {
  sharedStatus = s;
  subscribers.forEach((fn) => fn(s));
}

function startPolling(intervalMs: number) {
  if (pollStarted) return;
  pollStarted = true;

  const config = getConfig();
  const check = async () => {
    try {
      const res = await getChatStatus(config);
      notify(res.online ? 'online' : 'offline');
    } catch {
      // keep current status
    }
  };

  check();
  pollTimer = setInterval(check, intervalMs);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  pollStarted = false;
  sharedStatus = 'unknown';
}

/**
 * Hook: returns real-time chat operator status.
 * Only active for native provider — polls /api/w/chat/status.
 * For external providers (PHPLive, Deskpro) returns 'unknown' (they manage their own UI).
 */
export function useChatStatus(): ChatStatus {
  const [status, setStatus] = useState<ChatStatus>(sharedStatus);
  const config = getConfig();
  const livechat = config.support?.livechat;

  useEffect(() => {
    if (!livechat || livechat.enabled === false) return;
    if (livechat.provider !== 'native') return;

    subscribers.add(setStatus);
    startPolling(livechat.poll_interval ?? 30000);

    return () => {
      subscribers.delete(setStatus);
      if (subscribers.size === 0) stopPolling();
    };
  }, [livechat?.provider, livechat?.enabled]);

  return livechat?.provider === 'native' ? status : 'unknown';
}
